/**
 * Single source of truth for the sidebar-section based agent permissions.
 *
 * Each section mirrors a top-level group in the dashboard sidebar
 * (see components-next/sidebar/Sidebar.vue). The agent create/edit modal
 * renders itself from this catalog, so adding a new entry here makes it
 * available in the permission selector automatically.
 *
 * Child `access`:
 *  - 'view'     -> access only (single checkbox, stored as "view")
 *  - 'editable' -> supports create/edit (checkbox + read/edit toggle, stored as "read" | "edit")
 *
 * Optional `feature` gates the entry behind an account feature flag.
 */

export const PERMISSION_LEVELS = {
  VIEW: 'view',
  READ: 'read',
  EDIT: 'edit',
};

export const SIDEBAR_PERMISSION_SECTIONS = [
  {
    id: 'conversations',
    labelKey: 'SIDEBAR.CONVERSATIONS',
    icon: 'i-lucide-message-circle',
    children: [
      { id: 'all_conversations', labelKey: 'SIDEBAR.ALL_CONVERSATIONS', access: 'view' },
      { id: 'kanban', labelKey: 'SIDEBAR.KANBAN', access: 'editable', feature: 'kanban_board' },
    ],
  },
  {
    id: 'calendar',
    labelKey: 'SIDEBAR.CALENDAR',
    icon: 'i-lucide-calendar',
    feature: 'calendar',
    children: [
      { id: 'calendar', labelKey: 'SIDEBAR.CALENDAR', access: 'editable', feature: 'calendar' },
    ],
  },
  {
    id: 'contacts',
    labelKey: 'SIDEBAR.CONTACTS',
    icon: 'i-lucide-contact',
    children: [
      { id: 'contacts', labelKey: 'SIDEBAR.ALL_CONTACTS', access: 'editable' },
    ],
  },
  {
    id: 'tools',
    labelKey: 'SIDEBAR.TOOLS',
    icon: 'i-lucide-wrench',
    children: [
      { id: 'boletos', labelKey: 'SIDEBAR.TOOLS_BOLETOS', access: 'editable', feature: 'tool_boletos' },
      { id: 'wa_campaigns', labelKey: 'SIDEBAR.TOOLS_WA_CAMPAIGNS', access: 'editable', feature: 'tool_wa_campaigns' },
      { id: 'wa_catalog', labelKey: 'SIDEBAR.TOOLS_WA_CATALOG', access: 'view', feature: 'wa_catalog' },
      { id: 'payment_hub', labelKey: 'SIDEBAR.TOOLS_PAYMENT_HUB', access: 'view', feature: 'payment_hub' },
      { id: 'viper_printer', labelKey: 'SIDEBAR.TOOLS_VIPER_PRINTER', access: 'view', feature: 'viper_printer' },
    ],
  },
  {
    id: 'reports',
    labelKey: 'SIDEBAR.REPORTS',
    icon: 'i-lucide-chart-spline',
    children: [
      { id: 'reports', labelKey: 'SIDEBAR.REPORTS_OVERVIEW', access: 'view' },
      { id: 'custom_reports', labelKey: 'SIDEBAR.REPORTS_CUSTOM', access: 'editable', feature: 'tool_custom_reports' },
    ],
  },
  {
    id: 'campaigns',
    labelKey: 'SIDEBAR.CAMPAIGNS',
    icon: 'i-lucide-megaphone',
    children: [
      { id: 'campaigns', labelKey: 'SIDEBAR.CAMPAIGNS', access: 'editable' },
    ],
  },
  {
    id: 'help_center',
    labelKey: 'SIDEBAR.HELP_CENTER.TITLE',
    icon: 'i-lucide-library-big',
    children: [
      { id: 'knowledge_base', labelKey: 'SIDEBAR.HELP_CENTER.KNOWLEDGE_BASE', access: 'editable', feature: 'knowledge_base' },
      { id: 'help_center', labelKey: 'SIDEBAR.HELP_CENTER_SETTINGS', access: 'editable' },
    ],
  },
  {
    id: 'settings',
    labelKey: 'SIDEBAR.SETTINGS',
    icon: 'i-lucide-bolt',
    children: [
      { id: 'labels', labelKey: 'SIDEBAR.LABELS', access: 'editable' },
      { id: 'attributes', labelKey: 'SIDEBAR.CUSTOM_ATTRIBUTES', access: 'editable' },
      { id: 'automation', labelKey: 'SIDEBAR.AUTOMATION', access: 'editable' },
      { id: 'stages', labelKey: 'SIDEBAR.STAGES', access: 'editable', feature: 'agent_bots' },
      { id: 'response_templates', labelKey: 'SIDEBAR.KNOWLEDGE_BASE', access: 'editable', feature: 'agent_bots' },
      { id: 'pending_questions', labelKey: 'SIDEBAR.OMNI_PENDING_QUESTIONS', access: 'editable', feature: 'agent_bots' },
      { id: 'data_sources', labelKey: 'SIDEBAR.OMNI_DATA_SOURCES', access: 'editable', feature: 'agent_bots' },
      { id: 'macros', labelKey: 'SIDEBAR.MACROS', access: 'editable' },
      { id: 'canned_responses', labelKey: 'SIDEBAR.CANNED_RESPONSES', access: 'editable' },
    ],
  },
];

export default SIDEBAR_PERMISSION_SECTIONS;
